import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Minus, Plus, X, StickyNote } from 'lucide-react';
import {
  decrementLine,
  incrementLine,
  recordPlacedOrder,
  setLineNote,
  setOrderNote,
  selectLines,
  selectSubtotal,
  type DineRootState,
} from './dineSlice';
import { placeDineOrder } from './placeOrder';
import CustomerInfoSheet from './CustomerInfoSheet';

/**
 * Persistent bottom bar that grows into the "Your Order Summary"
 * bottom-sheet on tap (mirrors the reference screen).
 *
 * Two visual states:
 *   - collapsed: a sticky orange strip with "₹ subtotal — View Cart"
 *   - expanded:  full sheet listing every line with qty +/- buttons,
 *                per-line note input, order-level note input, and
 *                a white "Place Order" button on the orange bar.
 *
 * Pressing "Place Order" opens <CustomerInfoSheet/> (Name + Mobile
 * → OTP → submit order).
 */
export default function CartSheet() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams<{ tenantCode?: string; qrToken: string }>();
  const lines = useSelector(selectLines);
  const subtotal = useSelector(selectSubtotal);
  const orderNote = useSelector((s: DineRootState) => s.dine.orderNote);
  const customer = useSelector((s: DineRootState) => s.dine.customer);
  const table = useSelector((s: DineRootState) => s.dine.table);
  const [open, setOpen] = useState(false);
  const [checkout, setCheckout] = useState(false);
  const [placing, setPlacing] = useState(false);

  // A customer who already entered Name/Mobile (and passed/soft-passed
  // OTP) this session is "known" — `verifiedAt` is stamped once and kept
  // by redux-persist. Repeat orders should NOT re-ask for those details.
  const isKnownCustomer = Boolean(customer?.verifiedAt && customer?.phone);

  async function handlePlaceOrder() {
    setOpen(false);
    // First-time customer → collect Name/Mobile/OTP via the sheet.
    if (!isKnownCustomer || !customer) {
      setCheckout(true);
      return;
    }
    // Returning customer → place the order straight away.
    setPlacing(true);
    try {
      const orderId = await placeDineOrder({
        qrToken: params.qrToken ?? '',
        customer,
        lines,
        orderNote,
        table,
      });
      if (orderId) dispatch(recordPlacedOrder(orderId));
      navigate(`/dine/${params.tenantCode}/t/${params.qrToken}/orders`);
    } finally {
      setPlacing(false);
    }
  }

  if (lines.length === 0) return null;

  return (
    <>
      {/* Collapsed strip — clicking it expands the sheet. */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed inset-x-0 bottom-14 z-30 flex items-center justify-between bg-primary px-4 py-3 text-left text-primary-foreground shadow-lg"
        >
          <div>
            <div className="text-base font-semibold">₹ {subtotal.toFixed(2)}</div>
            <div className="text-[11px] uppercase tracking-wide opacity-80">
              {lines.length} {lines.length === 1 ? 'item' : 'items'} · Subtotal
            </div>
          </div>
          <span className="rounded-md bg-white/15 px-3 py-1.5 text-sm font-medium">
            View Cart
          </span>
        </button>
      )}

      {/* Expanded sheet */}
      {open && (
        <div className="fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-surface-card shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h2 className="text-base font-semibold text-content-primary">Your Order Summary</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="grid h-8 w-8 place-items-center rounded-full bg-surface-app text-content-secondary"
              >
                <X size={16} />
              </button>
            </div>

            <ul className="divide-y divide-dashed divide-border px-4">
              {lines.map((l) => (
                <li key={l.menuItemId} className="py-3">
                  <div className="flex items-start gap-3">
                    {/* NOTE: the green (#16a34a) / red (#b91c1c) hex values
                        below are the semantic Indian veg / non-veg
                        food-safety mark — NOT theme chrome. They are
                        intentionally hard-coded and must stay fixed across
                        themes. */}
                    <span
                      aria-label={l.isVeg === false ? 'Non-veg' : 'Veg'}
                      className="mt-1 inline-flex h-4 w-4 items-center justify-center rounded-sm border-2"
                      style={{ borderColor: l.isVeg === false ? '#b91c1c' : '#16a34a' }}
                    >
                      <span
                        className="block h-2 w-2 rounded-full"
                        style={{ background: l.isVeg === false ? '#b91c1c' : '#16a34a' }}
                      />
                    </span>
                    <div className="flex-1">
                      <div className="text-[15px] font-semibold text-content-primary">{l.name}</div>
                      <button
                        type="button"
                        onClick={() => {
                          const note = window.prompt('Add a note for this item', l.note ?? '');
                          if (note !== null)
                            dispatch(setLineNote({ menuItemId: l.menuItemId, note }));
                        }}
                        className="mt-1 inline-flex items-center gap-1 text-[12px] text-content-muted underline-offset-2 hover:underline"
                      >
                        <StickyNote size={12} /> {l.note ? 'Edit Note' : 'Add Note'}
                      </button>
                      {l.note && <p className="mt-1 text-[12px] italic text-content-muted">“{l.note}”</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <QtyStepper
                        qty={l.qty}
                        onInc={() => dispatch(incrementLine(l.menuItemId))}
                        onDec={() => dispatch(decrementLine(l.menuItemId))}
                      />
                      <div className="text-sm font-semibold text-content-primary">
                        ₹ {(l.qty * l.price).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="px-4 pb-2 pt-1">
              <input
                value={orderNote}
                onChange={(e) => dispatch(setOrderNote(e.target.value))}
                placeholder="Enter a note for the entire order"
                className="h-10 w-full rounded-md border border-border px-3 text-sm outline-none focus:border-primary"
              />
            </div>

            {/* Place order bar */}
            <div className="sticky bottom-0 flex items-center justify-between gap-3 bg-primary px-4 py-3 text-primary-foreground">
              <div>
                <div className="text-lg font-semibold">₹ {subtotal.toFixed(2)}</div>
                <div className="text-[11px] uppercase tracking-wide opacity-80">Subtotal</div>
              </div>
              <button
                type="button"
                onClick={handlePlaceOrder}
                disabled={placing}
                className="rounded-md bg-surface-card px-5 py-2 text-sm font-semibold text-primary disabled:opacity-60"
              >
                {placing ? 'Placing…' : 'Place Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {checkout && <CustomerInfoSheet onClose={() => setCheckout(false)} />}
    </>
  );
}

function QtyStepper({
  qty,
  onInc,
  onDec,
}: {
  qty: number;
  onInc: () => void;
  onDec: () => void;
}) {
  return (
    <div className="inline-flex items-center gap-3 rounded-md border border-primary px-2 py-1 text-sm font-semibold text-primary">
      <button type="button" onClick={onDec} aria-label="Decrease">
        <Minus size={14} />
      </button>
      <span className="min-w-[14px] text-center text-content-primary">{qty}</span>
      <button type="button" onClick={onInc} aria-label="Increase">
        <Plus size={14} />
      </button>
    </div>
  );
}
